import React, { memo, useState, useRef, useEffect } from "react";
import styles from "./MainModalNew.module.scss";
import { closeMainModal } from "../../../store/actions/profileActions";
import { connect } from "react-redux";
import { motion } from "framer-motion";
import ModalOverlay from "../ModalOverlay/ModalOverlay.tsx";
// SVG imports removed as they're not used in the new design
import { X, ScanFace, CheckCircle, AlertTriangle, AlertCircle, Mail } from "lucide-react";

const modalVariants = {
  hidden: { opacity: 0, scale: 1.1, y: 20 },
  visible: {
    opacity: 1,
    scale: 1.0,
    y: 0,
    transition: { 
      delay: 0.1,
      duration: 0.3,
      ease: "easeOut"
    },
  },
};

const MainModalNew = ({
  closeMainModal,
  mainModalConfig: {
    isSuccess,
    dismiss,
    title,
    body,
    confirmHandler,
    noHandler,
    customCloseMainModal,
    yesLabel = "Si",
    noLabel = "Volver",
    noActionNeeded,
    iconType,
  },
}) => {
  const [isButtonShrinking, setIsButtonShrinking] = useState(false);
  const [buttonWidth, setButtonWidth] = useState(null);

  const buttonRef = useRef(null);
  const timeoutRefs = useRef({ first: null, second: null });

  useEffect(() => {
    if (buttonRef.current && !buttonWidth) {
      setButtonWidth(buttonRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    const timeouts = timeoutRefs.current;
    return () => {
      if (timeouts.first) {
        clearTimeout(timeouts.first);
      }
      if (timeouts.second) {
        clearTimeout(timeouts.second);
      }
    };
  }, []);

  const handleClose = () => {
    if (customCloseMainModal) {
      customCloseMainModal();
    } else {
      closeMainModal();
    }
  };

  const renderIcon = (type) => {
    const icons = {
      success: <CheckCircle size={48} className={styles.successIcon} />,
      error: <AlertCircle size={48} className={styles.errorIcon} />,
      email: <Mail size={48} className={styles.emailIcon} />,
      warning: <AlertTriangle size={48} className={styles.warningIcon} />,
      scanFace: <ScanFace size={64} className={styles.scanFaceIcon} />,
    };
    return icons[type] || null;
  };

  const handleConfirm = () => {
    if (!confirmHandler || typeof confirmHandler !== 'function') {
      handleClose();
      return;
    }

    if (buttonWidth) {
      setIsButtonShrinking(true);
      timeoutRefs.current.first = setTimeout(() => {
        confirmHandler();
        timeoutRefs.current.second = setTimeout(() => {
          setIsButtonShrinking(false);
          setButtonWidth(null);
        }, 300);
      }, 300);
    } else {
      confirmHandler();
    }
  };

  const handleNo = () => {
    if (noHandler) {
      noHandler();
    } else {
      handleClose();
    }
  };

  return (
    <ModalOverlay 
      isOpen={true}
      onClose={dismiss ? closeMainModal : handleClose}
    >
      <motion.div
        className={styles.modal}
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
          <div className={styles.modalBody}>
            <button
              className={styles.closeButton}
              onClick={dismiss ? closeMainModal : handleClose}
              aria-label="Cerrar modal"
            >
              <X size={20} />
            </button>
            
            <div className={styles.content}>
              {iconType && (
                <div className={styles.iconContainer}>
                  {renderIcon(iconType)}
                </div>
              )}
              
              <h3 className={styles.title}>
                {title}
              </h3>
              
              <div className={styles.bodyText}>
                {body}
              </div>
            </div>
            
            {!isSuccess || !noActionNeeded ? (
              <div className={styles.options}>
                {isSuccess ? (
                  <button
                    className={`${styles.button} ${styles.secondaryButton}`}
                    onClick={handleClose}
                  >
                    {noLabel}
                  </button>
                ) : (
                  <>
                    <button
                      className={`${styles.button} ${styles.secondaryButton}`}
                      onClick={handleNo}
                    >
                      {noLabel}
                    </button>
                    <button
                      ref={buttonRef}
                      className={`${styles.button} ${styles.primaryButton}`}
                      onClick={handleConfirm}
                      style={{
                        width: buttonWidth ? `${buttonWidth}px` : 'auto',
                        transform: isButtonShrinking ? 'scaleX(0)' : 'scaleX(1)',
                        transformOrigin: 'center',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {yesLabel}
                    </button>
                  </>
                )}
              </div>
            ) : null}
          </div>
      </motion.div>
    </ModalOverlay>
  );
};

const mapStateToProps = (state) => ({
  mainModalConfig: state.general.mainModalConfig,
});

export default connect(mapStateToProps, { closeMainModal })(memo(MainModalNew));
